import * as React from 'react';
import { PortalFieldLabel } from 'components/FormControls';
import { IMeta } from 'models/common';
import renderIcon, { IconName } from 'helpers/renderIcon';
import { Tooltip } from 'components';
import { useOutsideAlerter } from 'components/Magazine/Article/ArticleComment/ArticleComment';
const classNames = require('classnames');
require('./style.css');

const TEXTAREA_USAGE = {
	portal: '',
	admin: 'portalAdminTextareaField',
};

interface IProps {
	usage?: 'portal' | 'admin';
	input?: React.InputHTMLAttributes<HTMLTextAreaElement>;
	label?: any;
	type?: string;
	rows?: number;
	meta?: IMeta;
	autoFocus?: any;
	readOnly?: boolean;
	isDisabled?: boolean;
	onChangeAction?: any;
	tooltipText?: string;
	tooltipPosition?: any;
	description?: string;
	information?: string;
	className?: string;
	iconName?: IconName;
	iconClassName?: string;
	maxLength?: number;
	iconAction?: () => void | undefined;
	customRef?: React.RefObject<HTMLTextAreaElement> | undefined;
	onBlurAction?: () => void | undefined;
	customIconSizeWidth?: number;
	customIconSizeHeight?: number;
	value?: string;
	height?: string;
	t?: (key: string) => string;
	onFocusAction?: () => void | undefined;
	disablePaddingOnFocus?: boolean;
	submitOnIconClick?: boolean;
	disableMargin: boolean;
	onKeyDownAction?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
}

export const PortalTextareaField = (props: IProps) => {
	const {
		iconName,
		iconClassName,
		iconAction,
		onBlurAction,
		customIconSizeWidth,
		customIconSizeHeight,
		submitOnIconClick,
	} = props;
	const [inputLength, setInputLength] = React.useState(0);
	const [disableMargin, setDisableMargin] = React.useState(props?.disableMargin ? true : false); // we disable margin when the input is not focused so that the input is not pushed down
	const fieldGroupRef = React.useRef<HTMLDivElement>(null);

	if (disableMargin) {
		useOutsideAlerter(fieldGroupRef, setDisableMargin, true); // we disable margin when the input is not focused so that the input is not pushed down
	} else {
		useOutsideAlerter(fieldGroupRef, setDisableMargin, false); // we need to use the hook regardless of the disableMargin state (https://stackoverflow.com/questions/53472795/uncaught-error-rendered-fewer-hooks-than-expected-this-may-be-caused-by-an-acc#:~:text=The%20problem%20is%20that%20in%20the%20first%20render%2C%203%20useState%20hooks%20were%20invoked%20%2D%20name%2C%20age%20and%20license%20but%20after%20the%20age%20is%20changed%20to%20a%20value%20below%2016%2C%20the%20useState%20for%20license%20is%20no%20longer%20invoked%2C%20resulting%20in%20only%20the%20first%202%20hooks%20being%20invoked.%20As%20the%20React%20docs%20state%3A)
	}

	let informationContainer: JSX.Element = null;
	if (typeof props.information !== 'undefined' && props.information !== null) {
		informationContainer = <div className="informationBlock">{props.information}</div>;
	}
	const usage = props.usage ? TEXTAREA_USAGE[props.usage] : TEXTAREA_USAGE.portal;

	let description: string | string[] = props.description || '';
	let status = '';

	if (props.meta?.touched) {
		if (props.meta?.warning) {
			status = 'withWarning';
			description = props.meta.warning;
		}
		if (props.meta?.error) {
			status = 'withError';
			description = props.meta.error;
		}
	}

	if (props.maxLength) {
		const maxLength = Number(props.maxLength);
		const value = String(props.input?.value || '');
		if (value.length > maxLength) {
			// FIXME translate
			description = 'Validation_Field_TooLong';
			status = 'withError';
		} else if (value.length <= maxLength) {
			description = [description as string, value.length + '/' + maxLength];
		}
	}

	return (
		<div className={classNames('fieldGroup', usage, status, props.className)} ref={fieldGroupRef}>
			{informationContainer}
			<label
				className={classNames(
					'inputHolder',
					'cursor-text-hover',
					props.disablePaddingOnFocus ? 'disablePaddingFocus' : '',
				)}
			>
				<textarea
					{...props.input}
					rows={props.rows}
					className={classNames(
						'inputField',
						{ ['inputError']: props.meta.touched && props.meta.error },
						{ value: props.input?.value },
						{ hasIconRight: iconName },
						disableMargin ? 'disableMargin' : '',
					)}
					autoFocus={props.autoFocus}
					readOnly={props.readOnly}
					disabled={props.isDisabled}
					ref={props.customRef}
					onInput={(e: React.FormEvent<HTMLTextAreaElement>) => {
						// Limit character amount
						setInputLength(e.currentTarget.value.length);
						const target = e.target as HTMLTextAreaElement;

						if (target.value.length > Number(props.maxLength)) {
							target.value = target.value.substring(0, Number(props.maxLength));
							e.preventDefault();
						}
					}}
					onChange={(e) => {
						if (props.onChangeAction) {
							props.onChangeAction(e);
						}
						if (props.input && props.input.onChange) {
							props.input.onChange(e);
						}
					}}
					onBlur={onBlurAction}
					style={props.height ? { height: props.height } : {}}
					onFocus={() => {
						if (props.onFocusAction) {
							props.onFocusAction();
							if (disableMargin) {
								setDisableMargin(false);
							}
						}
					}}
					onKeyDown={props?.onKeyDownAction}
				/>
				{iconName ? (
					submitOnIconClick ? (
						<button type="submit" className="submitButton" disabled={inputLength === 0}>
							{renderIcon(iconName, customIconSizeWidth || 22, customIconSizeHeight || 22, iconClassName, iconAction)}
						</button>
					) : (
						renderIcon(iconName, customIconSizeWidth || 22, customIconSizeHeight || 22, iconClassName, iconAction)
					)
				) : null}
				<PortalFieldLabel name={props.input.name} text={props.label} tag="div" />
				{props.tooltipText && props.tooltipText.length > 0 ? (
					<Tooltip position={props.tooltipPosition} message={props.tooltipText}>
						<a href="#" onClick={(e) => e.preventDefault()}>
							<img src={require('containers/Html/icons/tooltip-icon.png')} />
						</a>
					</Tooltip>
				) : null}
			</label>
			{description ? (
				<span className={'note' + (Array.isArray(description) ? ' split' : '')}>
					{Array.isArray(description) ? description.map((d, i) => <span key={i}>{d}</span>) : description}
				</span>
			) : null}
		</div>
	);
};
